import { Controller } from "stimulus"
import ga from "../utils/ga"
import { Consent, userHasOptedOutOfCookiesForCategory } from "../utils/consent"
import { setExperimentCookie, getExperimentCookie } from "../utils/cookies"
import experiments from "../stores/experiment_toggle_store"
import reduce from "lodash/reduce"
import isEmpty from "lodash/isEmpty"
import isEqual from "lodash/isEqual"
import forOwn from "lodash/forOwn"
import flatten from "lodash/flatten"
import pickBy from "lodash/pickBy"
import sample from "lodash/sample"
import times from "lodash/times"
import includes from "lodash/includes"

export default class extends Controller {
  static targets = ["variant", "cover"]
  static experimentsConfig

  initialize() {
    if (!this.userRevokedExperimentsConsent()) {
      this.experimentsConfig = this.parseActiveExperiments()
      this.runExperiments()
    }
    // always remove covers so the default can be seen
    // even when no experiment permission is given
    this.removeCovers()
  }

  shouldDoEnrolment() {
    return this.element.dataset.experimentsSkipEnrolment !== "true"
  }

  parseActiveExperiments() {
    const parsedConfig = JSON.parse(document.body.dataset.experiments)

    // Only return experiments that are in a running state
    return pickBy(parsedConfig, (experiment) => experiment.running === true)
  }

  userRevokedExperimentsConsent() {
    return userHasOptedOutOfCookiesForCategory(Consent.preferences)
  }

  independentExperiments() {
    return Object.values(this.experimentsConfig).filter((e) => isEmpty(e.requires))
  }

  dependentExperiments(enrolments) {
    const controller = this
    return Object.values(this.experimentsConfig).filter(
      (e) => !isEmpty(e.requires) && controller.requirementsMet(this.experimentsConfig, e.requires, enrolments)
    )
  }

  getExperimentTogglesFromUrlParams() {
    // ?experiment_toggles=activeToggle,!inactiveToggle
    const queryString = new URL(document.location.toString()).searchParams.get("experiment_toggles")
    if (!queryString) return

    const toggles = queryString.split(",")
    toggles.forEach((toggle) => {
      const toggleValue = this.toggleToValue(toggle)
      experiments.merge({ [toggleValue[0]]: toggleValue[1] })
    })
  }

  // turn "!toggleName" into ["toggleName", false] and "toggleName" into ["toggleName", true]
  toggleToValue(toggle) {
    if (toggle.charAt(0) === "!") {
      return [toggle.substring(1), false]
    } else {
      return [toggle, true]
    }
  }

  getExperimentTogglesFromEnrolments(enrolments) {
    Object.keys(enrolments).forEach((expId) => {
      const experimentToggles = this.findExperimentToggles(expId, enrolments[expId])
      experiments.merge(experimentToggles)
    })
  }

  findExperimentToggles(id, variant) {
    const configName = Object.keys(this.experimentsConfig).find((key) => this.experimentsConfig[key].id === id)
    const config = this.experimentsConfig[configName]
    return config?.variants?.[variant]
  }

  runExperiments() {
    const independentExperiments = this.independentExperiments()
    let independentEnrolments = {}

    if (!isEmpty(independentExperiments)) {
      independentEnrolments = this.setEnrolments(independentExperiments)
    }

    const dependentExperiments = this.dependentExperiments(independentEnrolments)
    let dependentEnrolments = {}

    if (!isEmpty(dependentExperiments)) {
      dependentEnrolments = this.setEnrolments(dependentExperiments)
    }

    const enrolments = { ...independentEnrolments, ...dependentEnrolments }

    if (this.shouldDoEnrolment()) {
      setExperimentCookie(enrolments)
    }

    this.getExperimentTogglesFromEnrolments(enrolments)
    this.getExperimentTogglesFromUrlParams()

    this.showSelectedVariants()

    this.reportGA(enrolments, [...independentExperiments, ...dependentExperiments])
  }

  showSelectedVariants() {
    this.variantTargets.forEach((domElement) => {
      this.toggleDomElementVisibility(domElement)
    })
  }

  removeCovers() {
    this.coverTargets.forEach((e) => e.remove())
  }

  shouldBeVisible(domElement) {
    return domElement.dataset.experimentToggle.split("&").every((toggle) => {
      const toggleValue = this.toggleToValue(toggle)

      return experiments.on(toggleValue[0]) === toggleValue[1]
    })
  }

  toggleDomElementVisibility(domElement) {
    if (this.shouldBeVisible(domElement)) {
      domElement.classList.remove("experiment-variant--hidden")
    } else {
      domElement.remove()
    }
  }

  requirementsMet(experiments, requiredExperiments, enrolments) {
    return reduce(
      requiredExperiments,
      function (memo, variants, name) {
        const requiredExperiment = experiments[name]

        if (!requiredExperiment) {
          return false
        }

        const requiredVariant = enrolments[requiredExperiment.id]

        return memo && includes(variants.variants, requiredVariant)
      },
      true
    )
  }

  selectVariant(experiment) {
    const weightedVariants = {}

    Object.keys(experiment.variants).forEach((variant, i) => {
      let weight = 1

      if (
        typeof experiment.variantsWeights !== "undefined" &&
        experiment.variantsWeights &&
        experiment.variantsWeights.length >= i
      ) {
        weight = experiment.variantsWeights[i]
      }

      weightedVariants[variant] = weight
    })

    const variants = []

    forOwn(weightedVariants, function (weight, variant) {
      variants.push(times(weight, () => variant))
    })

    return sample(flatten(variants))
  }

  setEnrolments(experiments) {
    const controller = this
    const cookieValues = getExperimentCookie(experiments)

    if (!this.shouldDoEnrolment()) {
      return cookieValues
    }

    const enrolments = reduce(
      experiments,
      (memo, experiment) => {
        let variant = cookieValues[experiment.id]

        if (!variant) {
          variant = controller.selectVariant(experiment)
        }

        memo[experiment.id] = variant

        return memo
      },
      {}
    )

    return enrolments
  }

  reportGA(enrolments, experiments) {
    if (!this.shouldDoEnrolment()) {
      return
    }

    let gaValue
    const actualEnrolments = getExperimentCookie(experiments)

    if (isEqual(enrolments, actualEnrolments)) {
      gaValue = Object.entries(actualEnrolments)
        .map(([key, value]) => `${key}.${value}`)
        .join("!")
    }
    if (!gaValue) {
      gaValue = null
    }

    ga("set", "exp", gaValue)
    ga("set", "dimension21", gaValue)
    ga("set", "dimension22", gaValue)
  }
}
